import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Autocomplete,
  Fab,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import laboratoryMethodsAPIService from '../../services/laboratoryMethodsAPIService';
import { alpha } from '@mui/material/styles';

interface DetectionLimit {
  limit: number;
  unit: string;
}

interface LaboratoryMethod {
  id: string;
  method_code: string;
  method_name: string;
  laboratory: string;
  category: string;
  method_type?: string;
  is_accredited: boolean;
  elements: string[];
  typical_tat_hours: number;
  cost_per_sample?: number;
  description?: string;
  is_active: boolean;
  special_routing?: Record<string, string>;
  detection_limits?: Record<string, DetectionLimit>;
}

const MethodManagement: React.FC = () => {
  const [methods, setMethods] = useState<LaboratoryMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMethod, setEditingMethod] = useState<LaboratoryMethod | null>(null);
  const [viewingMethod, setViewingMethod] = useState<LaboratoryMethod | null>(null);
  const [formData, setFormData] = useState<Partial<LaboratoryMethod>>({});

  // All available elements for selection (includes compounds and physico elements)
  const ALL_ELEMENTS = [
    // Standard elements
    'H', 'He', 'Li', 'Be', 'B', 'C', 'N', 'O', 'F', 'Ne',
    'Na', 'Mg', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar', 'K', 'Ca',
    'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn',
    'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr', 'Rb', 'Sr', 'Y', 'Zr',
    'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd', 'In', 'Sn',
    'Sb', 'Te', 'I', 'Xe', 'Cs', 'Ba', 'La', 'Ce', 'Pr', 'Nd',
    'Pm', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy', 'Ho', 'Er', 'Tm', 'Yb',
    'Lu', 'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg',
    'Tl', 'Pb', 'Bi', 'Po', 'At', 'Rn', 'Fr', 'Ra', 'Ac', 'Th',
    'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf', 'Es', 'Fm',
    'Md', 'No', 'Lr',
    // Compound elements (oxides, carbonates, fluorides)
    'Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'Na2O', 'P2O5', 'SiO2', 'TiO2',
    'CaCO3', 'CaF2',
    // Physico-Chimique elements
    'DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'D80'
  ];

  const LABORATORIES = [
    'PREPARATION_MECANIQUE',
    'LABORATOIRE_SAA', 
    'LABORATOIRE_ICP',
    'LABORATOIRE_AU',
    'LABORATOIRE_VOLUMETRIE',
    'LABORATOIRE_PHYSICO_CHIMIQUE'
  ];

  const CATEGORIES = [
    'MECHANICAL_PREP',
    'ATOMIC_ABSORPTION',
    'ICP_ANALYSIS', 
    'FIRE_ASSAY',
    'TITRATION',
    'GRAVIMETRIC'
  ];

  const METHOD_TYPES = [
    { value: 'METHODE_RAPIDE', label: 'Méthode Rapide', description: 'Affichée dans les raccourcis' },
    { value: 'METHODE_NORMALE', label: 'Méthode Normale', description: 'Suggérée selon les éléments sélectionnés' }
  ];

  useEffect(() => {
    loadMethods();
  }, []);

  const loadMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Loading methods from API...');
      const data = await laboratoryMethodsAPIService.getAllMethods();
      console.log('📊 Loaded methods:', data.length, 'methods');
      
      // Force state update by creating a new array reference
      setMethods([...data]);
      console.log('✅ Methods state updated with new array reference');
    } catch (err) {
      console.error('❌ Error loading methods:', err);
      setError('Erreur lors du chargement des méthodes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingMethod(null);
    setFormData({
      method_code: '',
      method_name: '',
      laboratory: '',
      category: '',
      is_accredited: false,
      elements: [],
      typical_tat_hours: 24,
      cost_per_sample: 0,
      description: '',
      is_active: true
    });
    setOpenDialog(true);
  };

  const handleEdit = (method: LaboratoryMethod) => {
    console.log('🔄 Opening edit dialog for method:', method.method_code);
    console.log('🔄 Method data:', method);
    console.log('🔄 Method type:', method.method_type);
    setEditingMethod(method);
    setFormData({ ...method });
    console.log('🔄 Form data set to:', { ...method });
    setOpenDialog(true);
  };

  const handleView = (method: LaboratoryMethod) => {
    setViewingMethod(method);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      console.log('🔄 Saving method - formData:', formData);
      console.log('🔄 Method type in formData:', formData.method_type);
      console.log('🔄 Editing method:', editingMethod?.method_code);
      
      if (editingMethod) {
        // Update existing method
        console.log('📝 Updating method:', editingMethod.id);
        console.log('📝 Data being sent to API:', formData);
        const result = await laboratoryMethodsAPIService.updateMethod(editingMethod.id, formData);
        console.log('✅ Update result:', result);
      } else {
        // Create new method
        console.log('➕ Creating new method');
        const result = await laboratoryMethodsAPIService.createMethod(formData);
        console.log('✅ Create result:', result);
      }
      
      setOpenDialog(false);
      console.log('🔄 Reloading methods...');
      await loadMethods();
      console.log('✅ Methods reloaded');
      
      // Force a small delay to ensure state updates are processed
      setTimeout(() => {
        console.log('🔄 Forcing UI refresh...');
        setLoading(false);
      }, 100);
      
    } catch (err) {
      console.error('❌ Error saving method:', err);
      setError('Erreur lors de la sauvegarde');
      setLoading(false);
    }
  };

  const handleDelete = async (method: LaboratoryMethod) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la méthode ${method.method_code}?`)) {
      try {
        setLoading(true);
        await laboratoryMethodsAPIService.deleteMethod(method.id);
        await loadMethods();
      } catch (err) {
        console.error('Error deleting method:', err);
        setError('Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const getLaboratoryColor = (laboratory: string) => {
    const colors: Record<string, string> = {
      'LABORATOIRE_SAA': '#4CAF50',
      'LABORATOIRE_ICP': '#2196F3',
      'LABORATOIRE_AU': '#FFD700',
      'LABORATOIRE_VOLUMETRIE': '#9C27B0',
      'PREPARATION_MECANIQUE': '#FF9800',
      'LABORATOIRE_PHYSICO_CHIMIQUE': '#00BCD4'
    };
    return colors[laboratory] || '#607D8B';
  };

  const getLaboratoryDisplayName = (laboratory: string) => {
    const names: Record<string, string> = {
      'LABORATOIRE_SAA': 'SAA',
      'LABORATOIRE_ICP': 'ICP',
      'LABORATOIRE_AU': 'AU',
      'LABORATOIRE_VOLUMETRIE': 'Volumétrie',
      'PREPARATION_MECANIQUE': 'Préparation Mécanique',
      'LABORATOIRE_PHYSICO_CHIMIQUE': 'Physico-Chimique'
    };
    return names[laboratory] || laboratory;
  };
  
  // Count detection limits
  const getDetectionLimitsCount = (method: LaboratoryMethod) => {
    if (!method.detection_limits) return 0;
    return Object.keys(method.detection_limits).length;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress size={60} />
        <Typography sx={{ ml: 2 }}>Chargement des méthodes...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScienceIcon sx={{ fontSize: 32 }} />
          Gestion des Méthodes d'Analyse
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadMethods}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nouvelle Méthode
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Methods Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Code Méthode</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Laboratoire</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Éléments</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Limites Détection</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Accréditation</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>TAT (h)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {methods.map((method) => (
                  <TableRow key={`${method.id}-${method.method_type}-${method.updated_at}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {method.method_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {method.method_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={method.method_type === 'METHODE_RAPIDE' ? '⚡ Rapide' : 'Normale'}
                        size="small"
                        color={method.method_type === 'METHODE_RAPIDE' ? 'primary' : 'default'}
                        sx={{
                          fontWeight: 'bold',
                          ...(method.method_type === 'METHODE_RAPIDE' && {
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2'
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLaboratoryDisplayName(method.laboratory)}
                        size="small"
                        sx={{
                          backgroundColor: alpha(getLaboratoryColor(method.laboratory), 0.1),
                          color: getLaboratoryColor(method.laboratory),
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${method.elements?.length || 0} éléments`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleView(method)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      {getDetectionLimitsCount(method) > 0 ? (
                        <Chip
                          label={`${getDetectionLimitsCount(method)} limites`}
                          size="small"
                          color="info"
                          variant="outlined"
                          onClick={() => handleView(method)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <Chip
                          label="Non définies"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {method.is_accredited ? (
                        <Chip
                          icon={<VerifiedIcon />}
                          label="Accrédité"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<WarningIcon />}
                          label="Non accrédité"
                          size="small"
                          color="warning"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {method.typical_tat_hours}h
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Voir détails">
                          <IconButton
                            size="small"
                            onClick={() => handleView(method)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(method)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(method)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {methods.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">
                Aucune méthode trouvée
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMethod ? 'Modifier la méthode' : 'Créer une nouvelle méthode'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code méthode"
                value={formData.method_code || ''}
                onChange={(e) => setFormData({ ...formData, method_code: e.target.value })}
                placeholder="ex: D4A-ICP- PRE.MO/ANA/038"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom de la méthode"
                value={formData.method_name || ''}
                onChange={(e) => setFormData({ ...formData, method_name: e.target.value })}
                placeholder="ex: ICP 55 Four Acid Digestion"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Laboratoire</InputLabel>
                <Select
                  value={formData.laboratory || ''}
                  onChange={(e) => setFormData({ ...formData, laboratory: e.target.value })}
                  label="Laboratoire"
                >
                  {LABORATORIES.map((lab) => (
                    <MenuItem key={lab} value={lab}>
                      {getLaboratoryDisplayName(lab)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Catégorie"
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type de Méthode</InputLabel>
                <Select
                  value={formData.method_type || 'METHODE_NORMALE'}
                  onChange={(e) => {
                    console.log('🔄 Method type changed from:', formData.method_type, 'to:', e.target.value);
                    console.log('🔄 Current formData before change:', formData);
                    const newFormData = { ...formData, method_type: e.target.value };
                    console.log('🔄 New formData after change:', newFormData);
                    setFormData(newFormData);
                  }}
                  label="Type de Méthode"
                >
                  {METHOD_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {type.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={ALL_ELEMENTS}
                value={formData.elements || []}
                onChange={(_, newValue) => setFormData({ ...formData, elements: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Éléments analysés"
                    placeholder="Sélectionnez les éléments"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="TAT (heures)"
                value={formData.typical_tat_hours || 24}
                onChange={(e) => setFormData({ ...formData, typical_tat_hours: parseInt(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Coût par échantillon"
                value={formData.cost_per_sample || 0}
                onChange={(e) => setFormData({ ...formData, cost_per_sample: parseFloat(e.target.value) })}
                InputProps={{ startAdornment: 'Dhs ' }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_accredited || false}
                    onChange={(e) => setFormData({ ...formData, is_accredited: e.target.checked })}
                  />
                }
                label="Méthode accréditée"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description détaillée de la méthode d'analyse..."
              />
            </Grid>

            {/* Detection Limits Section */}
            {formData.elements && formData.elements.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScienceIcon />
                  Limites de Détection
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Définissez la limite de détection pour chaque élément de cette méthode
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Élément</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Limite</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Unité</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.elements.map((element) => {
                        const currentLimit = formData.detection_limits?.[element] || { limit: 0, unit: 'ppm' };
                        return (
                          <TableRow key={element}>
                            <TableCell>
                              <Chip label={element} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={currentLimit.limit || ''}
                                onChange={(e) => {
                                  const newLimits = {
                                    ...(formData.detection_limits || {}),
                                    [element]: {
                                      ...currentLimit,
                                      limit: parseFloat(e.target.value) || 0
                                    }
                                  };
                                  setFormData({ ...formData, detection_limits: newLimits });
                                }}
                                inputProps={{ step: 0.01, min: 0 }}
                                sx={{ width: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                size="small"
                                value={currentLimit.unit || 'ppm'}
                                onChange={(e) => {
                                  const newLimits = {
                                    ...(formData.detection_limits || {}),
                                    [element]: {
                                      ...currentLimit,
                                      unit: e.target.value
                                    }
                                  };
                                  setFormData({ ...formData, detection_limits: newLimits });
                                }}
                                sx={{ width: 80 }}
                              >
                                <MenuItem value="ppm">ppm</MenuItem>
                                <MenuItem value="%">%</MenuItem>
                                <MenuItem value="g/t">g/t</MenuItem>
                                <MenuItem value="ppb">ppb</MenuItem>
                                <MenuItem value="mg/l">mg/l</MenuItem>
                              </Select>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
            Annuler
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            {editingMethod ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={!!viewingMethod}
        onClose={() => setViewingMethod(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Détails de la méthode: {viewingMethod?.method_code}
        </DialogTitle>
        <DialogContent>
          {viewingMethod && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {viewingMethod.method_name}
                </Typography>
                <Typography color="textSecondary" paragraph>
                  {viewingMethod.description}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Laboratoire
                </Typography>
                <Chip
                  label={getLaboratoryDisplayName(viewingMethod.laboratory)}
                  sx={{
                    backgroundColor: alpha(getLaboratoryColor(viewingMethod.laboratory), 0.1),
                    color: getLaboratoryColor(viewingMethod.laboratory),
                    fontWeight: 'bold'
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Accréditation
                </Typography>
                <Chip
                  icon={viewingMethod.is_accredited ? <VerifiedIcon /> : <WarningIcon />}
                  label={viewingMethod.is_accredited ? 'Accrédité' : 'Non accrédité'}
                  color={viewingMethod.is_accredited ? 'success' : 'warning'}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Éléments analysés ({viewingMethod.elements?.length || 0})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {viewingMethod.elements?.map((element) => (
                    <Chip
                      key={element}
                      label={element}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>

              {viewingMethod.special_routing && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Routage spécial
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    {Object.entries(viewingMethod.special_routing).map(([element, lab]) => (
                      <Typography key={element} variant="body2">
                        <strong>{element}</strong> → {getLaboratoryDisplayName(lab)}
                      </Typography>
                    ))}
                  </Paper>
                </Grid>
              )}

              {/* Detection Limits Display */}
              {viewingMethod.detection_limits && Object.keys(viewingMethod.detection_limits).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Limites de Détection
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Élément</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Limite</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Unité</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(viewingMethod.detection_limits).map(([element, limit]) => (
                          <TableRow key={element}>
                            <TableCell>
                              <Chip label={element} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{(limit as DetectionLimit).limit}</TableCell>
                            <TableCell>{(limit as DetectionLimit).unit}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingMethod(null)}>
            Fermer
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setViewingMethod(null);
              handleEdit(viewingMethod!);
            }}
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MethodManagement;
